import React, { useContext } from 'react';
import { Col, Descriptions, Row, Spin, Tabs, Tag, Typography } from 'antd';

import InitiativeSavingsTable from '@totem/components/services/optimize/v2/dashboard/InitiativeSavingsTable';
import MonthlyComparisonChart from '@totem/components/services/optimize/v2/dashboard/MonthlyComparisonChart';
import ProgramDashboardContext from '@totem/components/services/optimize/v2/dashboard/ProgramDashboardContext';
import RunningTrackerChart from '@totem/components/services/optimize/v2/dashboard/RunningTrackerChart';
import SavingsKPIs from '@totem/components/services/optimize/v2/dashboard/SavingsKPIs';
import SavingsMixDonut from '@totem/components/services/optimize/v2/dashboard/SavingsMixDonut';
import SavingsStackedChart from '@totem/components/services/optimize/v2/dashboard/SavingsStackedChart';
import { MonthlyPerformance } from '@totem/components/services/optimize/v2/dashboard/types';

const { Title, Text } = Typography;

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const TYPE_COLORS: Record<string, string> = {
  consumption: 'blue',
  demand: 'orange',
  combined: 'purple',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  complete: 'blue',
  archived: 'default',
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

const sumPerformance = (months: MonthlyPerformance[]) =>
  months.reduce(
    (acc, month) => ({
      energyCostSavings: acc.energyCostSavings + month.energyCostSavings,
      demandCostSavings: acc.demandCostSavings + month.demandCostSavings,
      kwhSaved: acc.kwhSaved + month.kwhSaved,
      peakKwReduced: acc.peakKwReduced + month.peakKwReduced,
    }),
    {
      energyCostSavings: 0,
      demandCostSavings: 0,
      kwhSaved: 0,
      peakKwReduced: 0,
    },
  );

const ProgramDashboardPanel = () => {
  const { dashboard, loading } = useContext(ProgramDashboardContext);

  if (!dashboard) {
    return <Spin spinning={loading} />;
  }

  const { program } = dashboard;
  const accounts = dashboard.accounts ?? [];
  const baselineMonths = dashboard.baselineMonths ?? [];
  const performanceMonths = dashboard.performanceMonths ?? [];
  const initiatives = dashboard.initiatives ?? [];

  const actualMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'actual',
  );
  const projectedMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'projected',
  );

  const totals = sumPerformance(performanceMonths);
  const totalCostSavings = totals.energyCostSavings + totals.demandCostSavings;

  const projectedAnnualSavings =
    actualMonths.length > 0
      ? Math.round((totalCostSavings / actualMonths.length) * 12)
      : undefined;

  // Primary rate info from first account
  const primaryAccount = accounts.length > 0 ? accounts[0] : null;
  const energyOnlyRate = primaryAccount?.rateInfo?.energyOnlyRate ?? 0;
  const demandRate = primaryAccount?.rateInfo?.demandRate ?? 0;

  const overviewTab = (
    <Row gutter={[0, 24]}>
      <Col span={24}>
        <SavingsKPIs
          totalCostSavings={totalCostSavings}
          totalEnergyCostSavings={totals.energyCostSavings}
          totalDemandCostSavings={totals.demandCostSavings}
          totalKwhSaved={totals.kwhSaved}
          totalPeakKwReduced={totals.peakKwReduced}
          projectedAnnualSavings={projectedAnnualSavings}
          monthsActual={actualMonths.length}
          monthsProjected={projectedMonths.length}
        />
      </Col>
      <Col xs={24} lg={10}>
        <SavingsMixDonut
          totalEnergyCostSavings={totals.energyCostSavings}
          totalDemandCostSavings={totals.demandCostSavings}
        />
      </Col>
      <Col xs={24} lg={14}>
        <Descriptions
          title="Baseline Conditions"
          size="small"
          column={{ xs: 1, sm: 2 }}
          bordered
        >
          <Descriptions.Item label="Baseline Period">
            {formatDate(program.baselinePeriodStart)} –{' '}
            {formatDate(program.baselinePeriodEnd)}
          </Descriptions.Item>
          <Descriptions.Item label="Occupancy">
            {program.occupancyStatus || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Lease Hours">
            {program.baselineConditions?.leaseHours || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="HVAC Schedule">
            {program.baselineConditions?.hvacSchedule || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Lighting Schedule">
            {program.baselineConditions?.lightingSchedule || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Cleaning Schedule">
            {program.baselineConditions?.cleaningSchedule || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Staggered Startup">
            {program.baselineConditions?.staggeredStartupConfigured ? (
              <Tag color="green">Configured</Tag>
            ) : (
              <Tag>Not Configured</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Demand Limiting">
            {program.baselineConditions?.demandLimitingConfigured ? (
              <Tag color="green">Configured</Tag>
            ) : (
              <Tag>Not Configured</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Optimal Start/Stop">
            {program.baselineConditions?.optimalStartStopConfigured ? (
              <Tag color="green">Configured</Tag>
            ) : (
              <Tag>Not Configured</Tag>
            )}
          </Descriptions.Item>
          {energyOnlyRate > 0 && (
            <Descriptions.Item label="Energy Rate">
              ${energyOnlyRate.toFixed(4)}/kWh
            </Descriptions.Item>
          )}
          {demandRate > 0 && (
            <Descriptions.Item label="Demand Rate">
              ${demandRate.toFixed(2)}/kW
            </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
      {program.notes && (
        <Col span={24}>
          <Text type="secondary">{program.notes}</Text>
        </Col>
      )}
    </Row>
  );

  const consumptionTab = (
    <Row gutter={[0, 24]}>
      <Col span={24}>
        <Title level={5} style={{ margin: 0 }}>
          Monthly kWh — Baseline vs Actual
        </Title>
      </Col>
      <Col span={24}>
        <MonthlyComparisonChart
          baselineMonths={baselineMonths}
          performanceMonths={performanceMonths}
          metric="kwh"
          height={340}
        />
      </Col>
      <Col span={24}>
        <Title level={5} style={{ margin: 0 }}>
          Monthly Energy Cost Savings
        </Title>
      </Col>
      <Col span={24}>
        <SavingsStackedChart
          performanceMonths={performanceMonths}
          height={300}
        />
      </Col>
    </Row>
  );

  const demandTab = (
    <Row gutter={[0, 24]}>
      <Col span={24}>
        <Title level={5} style={{ margin: 0 }}>
          Monthly Peak kW — Baseline vs Actual
        </Title>
      </Col>
      <Col span={24}>
        <MonthlyComparisonChart
          baselineMonths={baselineMonths}
          performanceMonths={performanceMonths}
          metric="peakKw"
          height={340}
        />
      </Col>
      {initiatives.length > 0 && (
        <>
          <Col span={24}>
            <Title level={5} style={{ margin: 0 }}>
              Initiative Savings Attribution
            </Title>
          </Col>
          <Col span={24}>
            <InitiativeSavingsTable
              initiatives={initiatives}
              performanceMonths={performanceMonths}
            />
          </Col>
        </>
      )}
    </Row>
  );

  const trackerTab = (
    <Row gutter={[0, 24]}>
      <Col span={24}>
        <Title level={5} style={{ margin: 0 }}>
          Cumulative Cost Savings
        </Title>
      </Col>
      <Col span={24}>
        <RunningTrackerChart
          performanceMonths={performanceMonths}
          height={340}
        />
      </Col>
      {initiatives.length > 0 && (
        <>
          <Col span={24}>
            <Title level={5} style={{ margin: 0 }}>
              Savings by Initiative
            </Title>
          </Col>
          <Col span={24}>
            <InitiativeSavingsTable
              initiatives={initiatives}
              performanceMonths={performanceMonths}
            />
          </Col>
        </>
      )}
    </Row>
  );

  return (
    <Spin spinning={loading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Row align="middle" gutter={8}>
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                {program.name}
              </Title>
            </Col>
            <Col>
              <Tag color={STATUS_COLORS[program.status] ?? 'default'}>
                {program.status.charAt(0).toUpperCase() +
                  program.status.slice(1)}
              </Tag>
            </Col>
            {(program.types ?? []).map((typ) => (
              <Col key={typ}>
                <Tag color={TYPE_COLORS[typ] ?? 'default'}>
                  {typ.charAt(0).toUpperCase() + typ.slice(1)}
                </Tag>
              </Col>
            ))}
          </Row>
        </Col>
        <Col span={24}>
          <Tabs
            defaultActiveKey="overview"
            items={[
              { key: 'overview', label: 'Overview', children: overviewTab },
              {
                key: 'consumption',
                label: 'Consumption',
                children: consumptionTab,
              },
              { key: 'demand', label: 'Demand', children: demandTab },
              {
                key: 'tracker',
                label: 'Running Tracker',
                children: trackerTab,
              },
            ]}
          />
        </Col>
      </Row>
    </Spin>
  );
};

export default ProgramDashboardPanel;
